import { useEffect, useState } from "react";
import { Sheet } from "./Sheet.tsx";
import { messages } from "../i18n/index.ts";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  /**
   * Vazgeçme düğmesinin metni.
   *
   * Varsayılan "Oyunda kal": bu pencere ilk olarak oyundan çıkış onayı için
   * yazılmıştı. Oyunla ilgisi olmayan yerlerde (arkadaş çıkarma gibi) bu metin
   * anlamsız kalıyor, o yüzden değiştirilebilir.
   */
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  accent?: "mint" | "flame" | "amber";
  /**
   * Onay düğmesinin açılması için beklenecek süre.
   * Geri tuşuna refleksle iki kez basıp maçı kazara terk etmeyi önler.
   */
  confirmDelayMs?: number;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = messages.common.stayInGame,
  onConfirm,
  onCancel,
  busy = false,
  accent = "flame",
  confirmDelayMs = 0,
}: ConfirmDialogProps) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!open || confirmDelayMs <= 0) {
      setRemaining(0);
      return;
    }

    const deadline = Date.now() + confirmDelayMs;
    setRemaining(Math.ceil(confirmDelayMs / 1000));

    const id = window.setInterval(() => {
      const left = Math.max(0, deadline - Date.now());
      setRemaining(Math.ceil(left / 1000));
      if (left <= 0) window.clearInterval(id);
    }, 200);

    return () => window.clearInterval(id);
  }, [open, confirmDelayMs]);

  const waiting = remaining > 0;
  const variant =
    accent === "flame"
      ? "btn-primary--flame"
      : accent === "amber"
        ? "btn-primary--amber"
        : "";

  return (
    <Sheet
      open={open}
      title={title}
      accent={accent}
      placement="center"
      onClose={busy ? () => undefined : onCancel}
    >
      <p className="text-sm leading-relaxed text-muted">{message}</p>
      <div className="mt-6 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`btn-primary ${variant}`}
          onClick={onConfirm}
          disabled={busy || waiting}
        >
          {busy ? messages.common.processing : waiting ? `${confirmLabel} (${remaining})` : confirmLabel}
        </button>
      </div>
    </Sheet>
  );
}
